import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  FilterAltOffRounded,
  LocalShippingOutlined,
  PaymentsOutlined,
  ReceiptLongOutlined,
  SearchRounded,
} from '@mui/icons-material';
import DateField from '../components/DateField';
import DataTable from '../components/DataTable';
import AppPagination from '../components/AppPagination';
import { cancelVenta, fetchVentas, searchVentas, quickUpdateVenta } from '../api/ventas';
import { useLoading } from '../contexts/LoadingContext';
import { toast } from '../utils/alerts';

const ESTADOS_ENVIO = [
  { value: '', label: 'Todos' },
  { value: 'pendiente_envio', label: 'Pendiente de envío' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'recibido', label: 'Recibido' },
];

const isLocked = (row) => ['cancelada', 'finalizada'].includes((row?.estado_venta || '').toLowerCase());
const formatMoney = (value) => `$ ${Number(value || 0).toFixed(2)}`;

const getIsAdmin = () => {
  try {
    const stored = localStorage.getItem('user');
    if (!stored) return false;
    const user = JSON.parse(stored);
    return user.rol === 'admin' || user.rol_id === 1;
  } catch {
    return false;
  }
};

const labelFor = (value) => {
  const labels = {
    pendiente_pago: 'Pendiente de pago',
    pagada: 'Pagada',
    pendiente_envio: 'Pendiente de envío',
    enviado: 'Enviado',
    recibido: 'Recibido',
    activa: 'Activa',
    finalizada: 'Finalizada',
    cancelada: 'Cancelada',
    transferencia: 'Transferencia',
    contra_entrega: 'Contra entrega',
  };

  const normalized = String(value || '').toLowerCase();
  return labels[normalized] || String(value || '—').replaceAll('_', ' ');
};

const paymentMeta = (value) => {
  const current = String(value || '').toLowerCase();
  return {
    color: current === 'pagada' ? 'success' : 'warning',
    next: current === 'pagada' ? 'pendiente_pago' : 'pagada',
  };
};

const shippingMeta = (value) => {
  const current = String(value || '').toLowerCase();
  if (current === 'recibido') return { color: 'success', next: 'pendiente_envio' };
  if (current === 'enviado') return { color: 'info', next: 'recibido' };
  return { color: 'warning', next: 'enviado' };
};

const saleMeta = (value) => {
  const current = String(value || '').toLowerCase();
  if (current === 'finalizada') return 'success';
  if (current === 'cancelada') return 'error';
  return 'info';
};

export default function Ventas() {
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codigo, setCodigo] = useState('');
  const [fecha, setFecha] = useState('');
  const [estadoEnvio, setEstadoEnvio] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [confirmData, setConfirmData] = useState(null);

  const nav = useNavigate();
  const isAdmin = getIsAdmin();
  const { start, stop } = useLoading();
  const stoppedOnceRef = useRef(false);

  const formatVentas = (response) => {
    if (Array.isArray(response)) {
      setTotal(response.length);
      return response;
    }

    const data = Array.isArray(response?.data) ? response.data : [];
    const pagination = response?.pagination;
    if (pagination) {
      setTotal(Number(pagination.total || 0));
      if (pagination.page) setPage(pagination.page);
      if (pagination.limit) setLimit(pagination.limit);
    }
    return data;
  };

  const loadVentas = async (options = {}) => {
    setLoading(true);
    try {
      const data = await fetchVentas({ page, limit, ...(options || {}) });
      setVentas(formatVentas(data));
    } catch (error) {
      console.error('Error cargando ventas', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async ({ codigo: searchCode, fecha: searchDate, estado_envio: shippingState, opts = {} }) => {
    setLoading(true);
    try {
      const data = await searchVentas({
        codigo: searchCode,
        fecha: searchDate,
        estado_envio: shippingState,
        page,
        limit,
        ...(opts || {}),
      });
      setVentas(formatVentas(data));
    } catch (error) {
      console.error('Error buscando ventas', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadVentas();
      } finally {
        if (!stoppedOnceRef.current) {
          stop();
          stoppedOnceRef.current = true;
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const hasFilters = codigo || fecha || estadoEnvio;
      setPage(1);
      if (hasFilters) {
        handleSearch({ codigo, fecha, estado_envio: estadoEnvio, opts: { page: 1 } });
      } else {
        loadVentas({ page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo, fecha, estadoEnvio]);

  useEffect(() => {
    const hasFilters = codigo || fecha || estadoEnvio;
    if (hasFilters) handleSearch({ codigo, fecha, estado_envio: estadoEnvio });
    else loadVentas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const clearFilters = async () => {
    setCodigo('');
    setFecha('');
    setEstadoEnvio('');
    setPage(1);
    await loadVentas({ page: 1 });
  };

  const handleConfirm = async () => {
    if (!confirmData) return;
    const { row, field, next } = confirmData;

    try {
      const updated = await quickUpdateVenta(row.id, { [field]: next });
      toast.ok('Estado actualizado', updated?.mensaje || 'El estado se actualizó correctamente.');
      setVentas((current) => current.map((item) => (item.id === row.id ? { ...item, ...updated } : item)));
    } catch (error) {
      toast.error('Error', 'No se pudo actualizar el estado.');
      console.error(error);
    } finally {
      setConfirmData(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Código',
        accessor: (row) => (
          <Typography component="span" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.codigo || `#${row.id}`}
          </Typography>
        ),
        width: 140,
        sticky: 'left',
      },
      {
        Header: 'Cliente',
        accessor: (row) => row.cliente_nombre || 'Cliente no registrado',
        width: 210,
      },
      {
        Header: 'Fecha',
        accessor: (row) => <DateField value={row.fecha} empty="—" />,
        width: 128,
      },
      {
        Header: 'Pago',
        width: 155,
        accessor: (row) => {
          const meta = paymentMeta(row.estado_pago);
          const disabled = isLocked(row);
          return (
            <Tooltip title={disabled ? 'La venta ya está cerrada' : `Cambiar a ${labelFor(meta.next)}`}>
              <span>
                <Chip
                  aria-label={`Estado de pago: ${labelFor(row.estado_pago)}`}
                  label={labelFor(row.estado_pago)}
                  variant="outlined"
                  color={meta.color}
                  size="small"
                  clickable={!disabled}
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    setConfirmData({
                      row,
                      field: 'estado_pago',
                      next: meta.next,
                      title: 'Cambiar estado de pago',
                      message: `¿Deseas cambiar el estado de pago a “${labelFor(meta.next)}”?`,
                    });
                  }}
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        Header: 'Envío',
        width: 160,
        accessor: (row) => {
          const meta = shippingMeta(row.estado_envio);
          const disabled = isLocked(row);
          return (
            <Tooltip title={disabled ? 'La venta ya está cerrada' : `Cambiar a ${labelFor(meta.next)}`}>
              <span>
                <Chip
                  aria-label={`Estado de envío: ${labelFor(row.estado_envio)}`}
                  label={labelFor(row.estado_envio)}
                  variant="outlined"
                  color={meta.color}
                  size="small"
                  clickable={!disabled}
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    setConfirmData({
                      row,
                      field: 'estado_envio',
                      next: meta.next,
                      title: 'Cambiar estado de envío',
                      message: `¿Deseas cambiar el estado de envío a “${labelFor(meta.next)}”?`,
                    });
                  }}
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        Header: 'Transportista / encomienda',
        width: 215,
        accessor: (row) => (
          <Box>
            <Typography component="span" variant="body2" fontWeight={700}>
              {row.transportista_nombre || 'Sin transportista'}
            </Typography>
            <Typography display="block" variant="caption" color="text.secondary">
              Costo de envío: {formatMoney(row.costo_envio_proveedor)}
            </Typography>
          </Box>
        ),
      },
      {
        Header: 'Estado',
        accessor: (row) => (
          <Chip
            label={labelFor(row.estado_venta)}
            color={saleMeta(row.estado_venta)}
            size="small"
            variant="outlined"
          />
        ),
        width: 140,
      },
      {
        Header: 'Total venta',
        accessor: (row) => (
          <Typography component="span" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(row.total_venta)}
          </Typography>
        ),
        align: 'right',
        width: 145,
      },
      {
        Header: 'Ganancia neta',
        accessor: (row) => {
          const profit = Number(row.ganancia_neta || 0);
          return (
            <Typography
              component="span"
              fontWeight={800}
              sx={{ color: profit < 0 ? 'error.main' : 'success.main', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatMoney(profit)}
            </Typography>
          );
        },
        align: 'right',
        width: 155,
      },
    ],
    []
  );

  const hasFilters = Boolean(codigo || fecha || estadoEnvio);

  return (
    <>
      <Dialog open={Boolean(confirmData)} onClose={() => setConfirmData(null)} fullWidth maxWidth="xs">
        <DialogTitle>{confirmData?.title || 'Confirmar cambio'}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">{confirmData?.message || ''}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmData(null)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2.5 }, pb: { xs: 3, md: 4 } }}>
        <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptLongOutlined color="primary" />
                <Typography variant="h5">Ventas</Typography>
                <Chip size="small" color="primary" variant="outlined" label={`${total} registros`} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Consulta, cobra y da seguimiento a cada venta.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => {
                start();
                nav('/ventas/nuevo');
              }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              Nueva venta
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }}>
            <TextField
              label="Buscar por código"
              placeholder="Ej. V-00125"
              variant="outlined"
              size="small"
              value={codigo}
              onChange={(event) => setCodigo(event.target.value)}
              disabled={loading}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Fecha"
              type="date"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fecha}
              onChange={(event) => setFecha(event.target.value)}
              disabled={loading}
              sx={{ minWidth: { lg: 180 } }}
            />
            <TextField
              select
              size="small"
              label="Estado de envío"
              value={estadoEnvio}
              onChange={(event) => setEstadoEnvio(event.target.value)}
              sx={{ minWidth: { lg: 205 } }}
              disabled={loading}
            >
              {ESTADOS_ENVIO.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {hasFilters && (
              <Button
                color="inherit"
                startIcon={<FilterAltOffRounded />}
                onClick={clearFilters}
                disabled={loading}
                sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', lg: 'center' } }}
              >
                Limpiar filtros
              </Button>
            )}
          </Stack>
        </Paper>

        <DataTable
          rows={ventas}
          columns={columns}
          loading={loading}
          maxHeight="min(58vh, 640px)"
          actionGuard={(row) => ({
            view: true,
            edit: !isLocked(row) || (isAdmin && String(row?.estado_venta || '').toLowerCase() === 'finalizada'),
            delete:
              String(row?.estado_venta || '').toLowerCase() === 'activa' ||
              (isAdmin && String(row?.estado_venta || '').toLowerCase() === 'finalizada'),
          })}
          onView={(row) => {
            start();
            nav(`/ventas/ver/${row.id}`);
          }}
          onEdit={(row) => {
            if (isLocked(row) && !(isAdmin && String(row?.estado_venta || '').toLowerCase() === 'finalizada')) {
              toast.warn('Venta bloqueada', 'Solo un administrador puede editar ventas finalizadas. Las canceladas no se pueden editar.');
              return;
            }
            start();
            nav(`/ventas/editar/${row.id}`);
          }}
          onDelete={(row) => {
            if (!isAdmin && isLocked(row)) return;
            if (
              window.confirm(
                '¿Cancelar esta venta? El inventario reservado se liberará y quedará un registro de la cancelación.'
              )
            ) {
              setLoading(true);
              cancelVenta(row.id)
                .then((updated) => {
                  toast.ok('Venta cancelada', 'El inventario se liberó correctamente.');
                  setVentas((current) =>
                    current.map((item) => (item.id === row.id ? { ...item, ...updated } : item))
                  );
                })
                .catch((error) => {
                  toast.error('No se pudo cancelar', error?.response?.data?.error || 'Inténtelo de nuevo.');
                  console.error('Error cancelando venta', error);
                })
                .finally(() => setLoading(false));
            }
          }}
        />

        <Paper sx={{ mt: 1.5, overflow: 'hidden' }}>
          <AppPagination
            count={total}
            page={Math.max(0, page - 1)}
            rowsPerPage={limit}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            onRowsPerPageChange={(event) => {
              setLimit(parseInt(event.target.value, 10) || 10);
              setPage(1);
            }}
          />
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
          <Chip icon={<PaymentsOutlined />} label="Ámbar: pendiente de pago" color="warning" variant="outlined" size="small" />
          <Chip icon={<LocalShippingOutlined />} label="Azul: envío en proceso" color="info" variant="outlined" size="small" />
        </Stack>
      </Container>
    </>
  );
}
